import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PageBanner from '@/components/site/PageBanner';
import ToolNav from '@/components/site/ToolNav';
import SEO from '@/components/site/SEO';
import {
  Plus, Trash2, Link, Check, ArrowRight, ChevronDown, ChevronUp,
  Save, ShieldCheck, ShieldOff, Lock, Unlock, Eye, EyeOff, Copy, Search, X,
} from 'lucide-react';

/* ─── EMI helper ─────────────────────────────────────────────────────────── */
function calcEMI(principal, annualRate, tenureYears) {
  const r = annualRate / 12 / 100;
  const n = tenureYears * 12;
  if (r <= 0 || n <= 0 || principal <= 0) return 0;
  return (principal * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
}

/* ─── Constants ──────────────────────────────────────────────────────────── */
const COLORS = [
  { bg: '#1e73be', light: '#e8f2fc' },
  { bg: '#0f3d78', light: '#dce8f7' },
  { bg: '#2ea690', light: '#daf4ef' },
  { bg: '#b45309', light: '#fef3c7' },
];

const fmt   = (n) => '₹ ' + Math.round(n).toLocaleString('en-IN');
const fmtNo = (n) => Math.round(n).toLocaleString('en-IN');

export const BANK_LS_KEY = 'finirio_bank_charges';
const GST            = 0.18;
const LOCK_PASSWORD  = 'Finirio1123';

/* ─── Display name helper ────────────────────────────────────────────────── */
export function bankDisplayName(bank) {
  if (!bank) return '';
  return bank.name + (bank.product ? ` — ${bank.product}` : '');
}

/* ─── URL encode / decode ────────────────────────────────────────────────── */
let nextId = 3;
function encodeOptions(opts) {
  return opts.map((o) => `${o.amount}:${o.rate}:${o.tenure}:${encodeURIComponent(o.label)}:${o.bankId ?? ''}`).join('|');
}
function decodeOptions(str) {
  if (!str) return null;
  try {
    const parts = str.split('|');
    if (parts.length < 2 || parts.length > 4) return null;
    const opts = parts.map((part, idx) => {
      const [amount, rate, tenure, label, bankId] = part.split(':');
      const a = Number(amount), r = Number(rate), t = Number(tenure);
      if (!a || !r || !t) return null;
      return {
        id: idx + 1,
        label: label ? decodeURIComponent(label) : `Option ${String.fromCharCode(65 + idx)}`,
        amount: a, rate: r, tenure: t,
        bankId: bankId ? Number(bankId) : null,
      };
    });
    if (opts.some((o) => o === null)) return null;
    nextId = opts.length + 1;
    return opts;
  } catch { return null; }
}

const DEFAULT_OPTIONS = [
  { id: 1, label: 'Option A', amount: 5000000, rate: 8.5, tenure: 20, bankId: null },
  { id: 2, label: 'Option B', amount: 5000000, rate: 9.0, tenure: 20, bankId: null },
];

/* ─── Bank data helpers ──────────────────────────────────────────────────── */
let bankNextId = 20;
export const LOAN_PRODUCT_OPTIONS = [
  { value: '',                      label: 'Any / General' },
  { value: 'Home Loan',             label: 'Home Loan' },
  { value: 'Loan Against Property', label: 'Loan Against Property' },
  { value: 'Personal Loan',         label: 'Personal Loan' },
  { value: 'Business Loan',         label: 'Business Loan' },
];

export const SAMPLE_BANKS = [
  {
    id: bankNextId++, name: 'SBI', product: 'Home Loan',
    procFeeRate: 0.35, procFeeMin: 2000, procFeeMax: 10000,
    legalCharges: 5000, techCharges: 3500,
    modtStampRate: 0.3, noiCharge: 17750, docCharges: 5000,
    propertyInsurance: 'mandatory', propInsYears: 2, propInsPerYear: 2500,
    lifeInsurance: 'mandatory',
  },
  {
    id: bankNextId++, name: 'HDFC Bank', product: '',
    procFeeRate: 0.5, procFeeMin: 3000, procFeeMax: 25000,
    legalCharges: 6500, techCharges: 4000,
    modtStampRate: 0.3, noiCharge: 17750, docCharges: 5000,
    propertyInsurance: 'mandatory', propInsYears: 2, propInsPerYear: 3000,
    lifeInsurance: 'not_mandatory',
  },
  {
    id: bankNextId++, name: 'ICICI Bank', product: '',
    procFeeRate: 0.5, procFeeMin: 3000, procFeeMax: 25000,
    legalCharges: 5500, techCharges: 3500,
    modtStampRate: 0.3, noiCharge: 17750, docCharges: 5000,
    propertyInsurance: 'not_mandatory', propInsYears: 0, propInsPerYear: 0,
    lifeInsurance: 'not_mandatory',
  },
  {
    id: bankNextId++, name: 'Axis Bank', product: '',
    procFeeRate: 1.0, procFeeMin: 5000, procFeeMax: 25000,
    legalCharges: 5000, techCharges: 3000,
    modtStampRate: 0.3, noiCharge: 17750, docCharges: 5000,
    propertyInsurance: 'not_mandatory', propInsYears: 0, propInsPerYear: 0,
    lifeInsurance: 'not_mandatory',
  },
  {
    id: bankNextId++, name: 'Kotak', product: '',
    procFeeRate: 0.5, procFeeMin: 5000, procFeeMax: 25000,
    legalCharges: 5000, techCharges: 3000,
    modtStampRate: 0.3, noiCharge: 17750, docCharges: 5000,
    propertyInsurance: 'not_mandatory', propInsYears: 0, propInsPerYear: 0,
    lifeInsurance: 'not_mandatory',
  },
];

export function loadBanks() {
  try {
    const d = JSON.parse(localStorage.getItem(BANK_LS_KEY) || 'null');
    if (Array.isArray(d) && d.length > 0) {
      bankNextId = Math.max(...d.map((b) => b.id)) + 1;
      return d.map((b) => ({
        procFeeMin: 0, procFeeMax: 0, modtStampRate: 0.3,
        noiCharge: 17750, docCharges: 5000,
        propInsYears: 2, propInsPerYear: 2500,
        lifeInsurance: 'not_mandatory',
        product: '',   // back-fill for older saved data
        ...b,
      }));
    }
  } catch {}
  return SAMPLE_BANKS;
}

function calcProcFee(amount, bank) {
  const base   = (amount * (bank.procFeeRate || 0)) / 100;
  const min    = bank.procFeeMin || 0;
  const maxCap = bank.procFeeMax > 0 ? bank.procFeeMax : Infinity;
  return Math.min(Math.max(base, min), maxCap) * (1 + GST);
}

function calcBankCharges(amount, bank) {
  const procFee = calcProcFee(amount, bank);
  const modt    = (amount * (bank.modtStampRate ?? 0.3)) / 100;
  const noi     = bank.noiCharge    ?? 17750;
  const legal   = bank.legalCharges ?? 0;
  const tech    = bank.techCharges  ?? 0;
  const doc     = bank.docCharges   ?? 5000;
  const propIns = bank.propertyInsurance === 'mandatory'
    ? (bank.propInsYears || 0) * (bank.propInsPerYear || 0) : 0;
  const total   = procFee + modt + noi + legal + tech + doc + propIns;
  return { procFee, modt, noi, legal, tech, doc, propIns, total };
}

/* ─── Bar chart ─────────────────────────────────────────────────────────── */
function niceStep(maxVal) {
  if (maxVal <= 0) return 1;
  const raw = maxVal / 4;
  const magnitude = Math.pow(10, Math.floor(Math.log10(raw)));
  const normalized = raw / magnitude;
  let nice = normalized < 1.5 ? 1 : normalized < 3.5 ? 2 : normalized < 7.5 ? 5 : 10;
  return nice * magnitude;
}
function fmtShort(n) {
  if (n >= 10000000) return '₹' + (n / 10000000).toFixed(1) + 'Cr';
  if (n >= 100000)   return '₹' + (n / 100000).toFixed(1) + 'L';
  if (n >= 1000)     return '₹' + (n / 1000).toFixed(0) + 'K';
  return '₹' + Math.round(n);
}
function ComparisonBarChart({ results }) {
  const metrics = [{ key: 'emi', label: 'Monthly EMI' }, { key: 'totalInterest', label: 'Total Interest' }];
  const chartH = 200, bW = 36, bG = 8, pL = 70, pT = 16, pB = 48, pR = 16;
  return (
    <div className="mt-8 card-lux p-5">
      <h3 className="text-sm font-semibold text-slate-700 mb-4">Visual Comparison</h3>
      <div className="flex flex-wrap gap-4 mb-4">
        {results.map((r, idx) => (
          <div key={r.id} className="flex items-center gap-1.5 text-xs text-slate-600">
            <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: COLORS[idx % COLORS.length].bg }} />
            {r.label}
          </div>
        ))}
      </div>
      <div className="overflow-x-auto">
        {metrics.map((metric) => {
          const values = results.map((r) => r[metric.key]);
          const maxVal = Math.max(...values);
          const yStep = niceStep(maxVal);
          const yMax = Math.ceil(maxVal / yStep) * yStep;
          const yTicks = [];
          for (let v = 0; v <= yMax; v += yStep) yTicks.push(v);
          const svgH = chartH + pT + pB;
          const svgW = pL + results.length * (bW + bG) - bG + pR;
          return (
            <div key={metric.key} className="mb-6">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">{metric.label}</p>
              <svg viewBox={`0 0 ${svgW} ${svgH}`} width={svgW} height={svgH} className="overflow-visible" style={{ maxWidth: '100%' }}>
                {yTicks.map((tick) => {
                  const y = pT + chartH - (tick / yMax) * chartH;
                  return <g key={tick}><line x1={pL} y1={y} x2={svgW - pR} y2={y} stroke="#e2e8f0" strokeWidth="1" /><text x={pL - 6} y={y + 4} textAnchor="end" fontSize="10" fill="#94a3b8">{fmtShort(tick)}</text></g>;
                })}
                {results.map((r, idx) => {
                  const val = r[metric.key];
                  const barH = maxVal > 0 ? (val / yMax) * chartH : 0;
                  const x = pL + idx * (bW + bG);
                  const y = pT + chartH - barH;
                  const color = COLORS[idx % COLORS.length];
                  return (
                    <g key={r.id}>
                      <rect x={x} y={y} width={bW} height={barH} rx="3" fill={color.bg} opacity="0.92" />
                      <text x={x + bW / 2} y={y - 5} textAnchor="middle" fontSize="9.5" fontWeight="600" fill={color.bg}>{fmtShort(val)}</text>
                      <text x={x + bW / 2} y={pT + chartH + 16} textAnchor="middle" fontSize="10" fill="#64748b">{r.label}</text>
                    </g>
                  );
                })}
                <line x1={pL} y1={pT + chartH} x2={svgW - pR} y2={pT + chartH} stroke="#cbd5e1" strokeWidth="1" />
              </svg>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Inline number input ────────────────────────────────────────────────── */
function NumInput({ value, onChange, step = 1, min = 0, max, className = '', style }) {
  return (
    <input
      type="number" step={step} min={min} max={max} value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      onFocus={(e) => e.target.select()}
      className={`h-8 px-1.5 border border-slate-200 rounded text-[12px] text-center outline-none focus:ring-1 focus:ring-[#1e73be]/40 ${className}`}
      style={style}
    />
  );
}

/* ─── Insurance toggle badge ─────────────────────────────────────────────── */
function InsBadge({ value, label = 'Property', onClick }) {
  const m = value === 'mandatory';
  return (
    <button onClick={onClick}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border transition whitespace-nowrap ${m ? 'bg-red-50 text-red-600 border-red-200 hover:bg-red-100' : 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100'}`}
      title="Click to toggle">
      {m ? <ShieldCheck className="w-2.5 h-2.5" /> : <ShieldOff className="w-2.5 h-2.5" />}
      {m ? 'Mandatory' : 'Not Req.'}
    </button>
  );
}

/* ─── Main Component ─────────────────────────────────────────────────────── */
export default function LoanComparisonPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const [options, setOptions] = useState(() => {
    const params = new URLSearchParams(location.search);
    return decodeOptions(params.get('opts')) || DEFAULT_OPTIONS;
  });
  const [linkCopied, setLinkCopied] = useState(false);

  /* Bank section */
  const [showBanks, setShowBanks]       = useState(false);
  const [bankRows, setBankRows]         = useState(loadBanks);
  const [bankSaved, setBankSaved]       = useState(false);
  const [bankUnlocked, setBankUnlocked] = useState(false);
  const [pwInput, setPwInput]           = useState('');
  const [pwError, setPwError]           = useState(false);
  const [showPw, setShowPw]             = useState(false);
  const [bankSearch, setBankSearch]     = useState('');

  /* Filtered bank rows for the table (search by name or product) */
  const filteredBankRows = useMemo(() => {
    const q = bankSearch.trim().toLowerCase();
    if (!q) return bankRows;
    return bankRows.filter((b) =>
      b.name.toLowerCase().includes(q) ||
      (b.product || '').toLowerCase().includes(q)
    );
  }, [bankRows, bankSearch]);

  /* URL sync */
  const syncTimer = useRef(null);
  useEffect(() => {
    clearTimeout(syncTimer.current);
    syncTimer.current = setTimeout(() => {
      const params = new URLSearchParams();
      params.set('opts', encodeOptions(options));
      navigate({ search: params.toString() }, { replace: true });
    }, 400);
    return () => clearTimeout(syncTimer.current);
  }, [options, navigate]);

  /* EMI results */
  const results = useMemo(() =>
    options.map((opt) => {
      const emi          = calcEMI(opt.amount, opt.rate, opt.tenure);
      const totalPayment = emi * opt.tenure * 12;
      const totalInterest= totalPayment - opt.amount;
      const bank = bankRows.find((b) => b.id === opt.bankId) || null;
      const charges = bank ? calcBankCharges(opt.amount, bank) : null;
      return { ...opt, emi, totalPayment, totalInterest, charges };
    }), [options, bankRows]);

  const anyBankSelected = results.some((r) => r.charges !== null);
  const bestEMI      = Math.min(...results.map((r) => r.emi));
  const bestInterest = Math.min(...results.map((r) => r.totalInterest));
  const bestTotal    = anyBankSelected ? Math.min(...results.filter((r) => r.charges).map((r) => r.totalPayment + r.charges.total)) : null;

  /* Option updates */
  function updateOption(id, field, value) {
    setOptions((prev) => prev.map((o) => (o.id === id ? { ...o, [field]: value } : o)));
  }
  function selectBank(id, bankId) {
    const bank = bankRows.find((b) => b.id === bankId);
    setOptions((prev) => prev.map((o) => {
      if (o.id !== id) return o;
      const isDefault = /^Option [A-D]$/.test(o.label);
      return { ...o, bankId, label: bank && isDefault ? bank.name : o.label };
    }));
  }
  function copyFieldToNext(fromIdx, field) {
    if (fromIdx >= options.length - 1) return;
    updateOption(options[fromIdx + 1].id, field, options[fromIdx][field]);
  }
  function addOption() {
    if (options.length >= 4) return;
    const label = String.fromCharCode(65 + options.length);
    setOptions((prev) => [...prev, { id: nextId++, label: `Option ${label}`, amount: 5000000, rate: 8.5, tenure: 20, bankId: null }]);
  }
  function removeOption(id) {
    if (options.length <= 2) return;
    setOptions((prev) => prev.filter((o) => o.id !== id));
  }

  /* Password unlock */
  function tryUnlock() {
    if (pwInput === LOCK_PASSWORD) { setBankUnlocked(true); setPwError(false); setPwInput(''); }
    else { setPwError(true); }
  }

  /* Bank CRUD */
  function updateBank(id, field, value) {
    setBankRows((prev) => prev.map((b) => (b.id === id ? { ...b, [field]: value } : b)));
    setBankSaved(false);
  }
  function toggleBankField(id, field) {
    setBankRows((prev) => prev.map((b) => b.id === id ? { ...b, [field]: b[field] === 'mandatory' ? 'not_mandatory' : 'mandatory' } : b));
    setBankSaved(false);
  }
  function addBank() {
    setBankRows((prev) => [...prev, {
      id: bankNextId++, name: 'New Bank', product: '',
      procFeeRate: 0.5, procFeeMin: 5000, procFeeMax: 25000,
      legalCharges: 5000, techCharges: 3000,
      modtStampRate: 0.3, noiCharge: 17750, docCharges: 5000,
      propertyInsurance: 'not_mandatory', propInsYears: 0, propInsPerYear: 0,
      lifeInsurance: 'not_mandatory',
    }]);
    setBankSaved(false);
  }
  function removeBank(id) {
    setBankRows((prev) => prev.filter((b) => b.id !== id));
    setBankSaved(false);
  }
  function saveBanks() {
    localStorage.setItem(BANK_LS_KEY, JSON.stringify(bankRows));
    setBankSaved(true);
    setTimeout(() => setBankSaved(false), 2500);
  }

  /* Share */
  function handleShareLink() {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    });
  }

  const refAmount = options[0]?.amount || 5000000;

  /* ── RENDER ── */
  return (
    <main>
      <SEO
        title="Loan Comparison Calculator | Compare Home Loan Options | Finirio"
        description="Compare multiple home loan options side by side with full charges breakdown per bank."
        path="/loan-comparison"
        keywords="loan comparison calculator, compare home loan, home loan emi comparison india"
      />
      <PageBanner title="Loan Comparison" crumbs={['Calci and Docs', 'Loan Comparison']} />
      <ToolNav />

      <section className="py-14 bg-white">
        <div className="max-w-[1240px] mx-auto px-4 lg:px-6">

          {/* Header */}
          <div className="mb-8 flex items-center justify-between flex-wrap gap-3">
            <div>
              <h2 className="font-heading text-xl font-bold text-[#0b2545]">Compare Loan Options</h2>
              <p className="text-slate-500 text-sm mt-1">Select a bank per option — charges auto-compute from saved rates. Use → to copy values.</p>
            </div>
            {options.length < 4 && (
              <button onClick={addOption} className="btn-lux !px-4 !py-2 !text-[13px]">
                <Plus className="w-4 h-4" /> Add Option
              </button>
            )}
          </div>

          {/* ── Option Cards ── */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-10">
            {options.map((opt, idx) => {
              const color   = COLORS[idx % COLORS.length];
              const hasNext = idx < options.length - 1;
              return (
                <div key={opt.id} className="card-lux overflow-hidden">
                  {/* Header */}
                  <div className="px-4 py-3 flex items-center justify-between" style={{ backgroundColor: color.bg }}>
                    <input value={opt.label} onChange={(e) => updateOption(opt.id, 'label', e.target.value)}
                      className="bg-transparent text-white font-semibold text-[15px] w-full outline-none" maxLength={20} />
                    {options.length > 2 && (
                      <button onClick={() => removeOption(opt.id)} className="ml-2 flex-shrink-0 text-white/70 hover:text-white">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  <div className="p-4 space-y-3">
                    {/* Bank selector — shows name + product */}
                    <div>
                      <label className="block text-slate-500 text-[11px] font-medium uppercase tracking-wide mb-1">Bank / Product</label>
                      <select
                        value={opt.bankId ?? ''}
                        onChange={(e) => selectBank(opt.id, e.target.value ? Number(e.target.value) : null)}
                        className="w-full h-9 px-2 border border-slate-200 rounded-md text-[13px] outline-none focus:ring-2 focus:ring-[#1e73be]/30 bg-white"
                      >
                        <option value="">— Select Bank —</option>
                        {bankRows.map((b) => (
                          <option key={b.id} value={b.id}>{bankDisplayName(b)}</option>
                        ))}
                      </select>
                    </div>

                    {/* Amount / Rate / Tenure */}
                    {[
                      { label: 'Loan Amount (₹)', field: 'amount', step: 100000, min: 100000 },
                      { label: 'Interest Rate (%)', field: 'rate',   step: 0.05,   min: 1, max: 30 },
                      { label: 'Tenure (Years)',    field: 'tenure', step: 1,      min: 1, max: 30 },
                    ].map(({ label, field, step, min, max }) => (
                      <div key={field}>
                        <label className="block text-slate-500 text-[11px] font-medium uppercase tracking-wide mb-1">{label}</label>
                        <div className="flex items-center gap-1.5">
                          <input type="number" step={step} min={min} max={max} value={opt[field]}
                            onChange={(e) => updateOption(opt.id, field, Number(e.target.value))}
                            onFocus={(e) => e.target.select()}
                            className="flex-1 min-w-0 h-9 px-3 border border-slate-200 rounded-md text-sm outline-none focus:ring-2 focus:ring-[#1e73be]/30" />
                          {hasNext && (
                            <button onClick={() => copyFieldToNext(idx, field)}
                              className="w-8 h-9 flex items-center justify-center rounded-md border border-slate-200 text-slate-400 hover:border-[#1e73be] hover:text-[#1e73be] hover:bg-[#eaf3fb] transition flex-shrink-0"
                              title={`Copy to ${options[idx + 1].label}`}>
                              <ArrowRight className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* ── Results Table ── */}
          <div className="card-lux overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left px-5 py-3 text-slate-500 font-medium text-[12px] uppercase tracking-wide bg-slate-50 w-44">Metric</th>
                    {results.map((r, idx) => {
                      const bank = bankRows.find((b) => b.id === r.bankId);
                      return (
                        <th key={r.id} className="px-5 py-3 text-center font-semibold text-white text-[13px]" style={{ backgroundColor: COLORS[idx % COLORS.length].bg }}>
                          {r.label}
                          {bank && (
                            <div className="text-[10px] font-normal opacity-80">
                              {bank.name}{bank.product ? ` — ${bank.product}` : ''}
                            </div>
                          )}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {/* EMI rows */}
                  {[
                    { label: 'Loan Amount',    key: 'amount' },
                    { label: 'Interest Rate',  key: 'rate',          suffix: '%' },
                    { label: 'Tenure',         key: 'tenure',        suffix: ' yrs' },
                    { label: 'Monthly EMI',    key: 'emi',           highlight: true, best: bestEMI },
                    { label: 'Total Interest', key: 'totalInterest', highlight: true, best: bestInterest },
                    { label: 'Total Payment (EMI)', key: 'totalPayment' },
                  ].map((row, ri) => (
                    <tr key={row.key} className={ri % 2 === 0 ? 'bg-white' : 'bg-slate-50/60'}>
                      <td className="px-5 py-3.5 text-slate-600 font-medium text-[13px]">{row.label}</td>
                      {results.map((r, idx) => {
                        const color = COLORS[idx % COLORS.length];
                        const val = r[row.key];
                        const formatted = row.suffix ? `${val}${row.suffix}` : fmt(val);
                        const isBest = row.best !== undefined && Math.abs(val - row.best) < 0.01;
                        return (
                          <td key={r.id} className="px-5 py-3.5 text-center">
                            <span className={`inline-block font-semibold ${row.highlight ? 'text-[15px]' : 'text-[14px]'}`}
                              style={{ color: row.highlight ? color.bg : '#334155' }}>{formatted}</span>
                            {isBest && <span className="ml-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full text-white" style={{ backgroundColor: '#2ea690' }}>BEST</span>}
                          </td>
                        );
                      })}
                    </tr>
                  ))}

                  {/* Charges divider */}
                  {anyBankSelected && (
                    <tr>
                      <td colSpan={results.length + 1} className="px-5 py-2 bg-slate-100 border-t border-slate-300">
                        <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">One-time Charges (from Bank Rates)</span>
                      </td>
                    </tr>
                  )}

                  {/* Charge rows */}
                  {anyBankSelected && [
                    { label: 'Processing Fee (incl. GST)', key: 'procFee' },
                    { label: 'MODT / Stamp Duty',          key: 'modt' },
                    { label: 'NOI — Notice of Intimation', key: 'noi' },
                    { label: 'Legal Charges',              key: 'legal' },
                    { label: 'Technical Charges',          key: 'tech' },
                    { label: 'Documentation Charges',      key: 'doc' },
                    { label: 'Property Insurance',         key: 'propIns' },
                  ].map((row, ri) => (
                    <tr key={row.key} className={ri % 2 === 0 ? 'bg-white' : 'bg-slate-50/60'}>
                      <td className="px-5 py-3 text-slate-600 text-[13px]">{row.label}</td>
                      {results.map((r) => (
                        <td key={r.id} className="px-5 py-3 text-center text-[14px] font-medium text-slate-700">
                          {r.charges ? fmt(r.charges[row.key]) : <span className="text-slate-300">—</span>}
                        </td>
                      ))}
                    </tr>
                  ))}

                  {/* Life insurance row */}
                  {anyBankSelected && (
                    <tr className="bg-white">
                      <td className="px-5 py-3 text-slate-600 text-[13px]">Life Insurance</td>
                      {results.map((r) => {
                        const bank = bankRows.find((b) => b.id === r.bankId);
                        const m = bank?.lifeInsurance === 'mandatory';
                        return (
                          <td key={r.id} className="px-5 py-3 text-center">
                            {bank
                              ? <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border ${m ? 'bg-red-50 text-red-600 border-red-200' : 'bg-green-50 text-green-700 border-green-200'}`}>
                                  {m ? <ShieldCheck className="w-3 h-3" /> : <ShieldOff className="w-3 h-3" />}
                                  {m ? 'Mandatory' : 'Not Required'}
                                </span>
                              : <span className="text-slate-300">—</span>}
                          </td>
                        );
                      })}
                    </tr>
                  )}

                  {/* Property insurance details row */}
                  {anyBankSelected && (
                    <tr className="bg-slate-50/60">
                      <td className="px-5 py-3 text-slate-600 text-[13px]">Property Insurance</td>
                      {results.map((r) => {
                        const bank = bankRows.find((b) => b.id === r.bankId);
                        const m = bank?.propertyInsurance === 'mandatory';
                        return (
                          <td key={r.id} className="px-5 py-3 text-center">
                            {bank
                              ? <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border ${m ? 'bg-red-50 text-red-600 border-red-200' : 'bg-green-50 text-green-700 border-green-200'}`}>
                                  {m ? <ShieldCheck className="w-3 h-3" /> : <ShieldOff className="w-3 h-3" />}
                                  {m ? `${bank.propInsYears}yr × ${fmt(bank.propInsPerYear)}/yr` : 'Not Required'}
                                </span>
                              : <span className="text-slate-300">—</span>}
                          </td>
                        );
                      })}
                    </tr>
                  )}

                  {/* True total */}
                  {anyBankSelected && (
                    <tr className="border-t-2 border-[#1e73be] bg-[#f0f7ff]">
                      <td className="px-5 py-4 font-bold text-[#0f3d78] text-[14px]">True Total Cost</td>
                      {results.map((r, idx) => {
                        if (!r.charges) return <td key={r.id} className="px-5 py-4 text-center text-slate-300 text-[14px]">—</td>;
                        const trueTotal = r.totalPayment + r.charges.total;
                        const color = COLORS[idx % COLORS.length];
                        const isBest = bestTotal !== null && Math.abs(trueTotal - bestTotal) < 1;
                        return (
                          <td key={r.id} className="px-5 py-4 text-center">
                            <span className="text-[17px] font-bold" style={{ color: color.bg }}>{fmt(trueTotal)}</span>
                            {isBest && <span className="ml-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full text-white" style={{ backgroundColor: '#2ea690' }}>BEST</span>}
                          </td>
                        );
                      })}
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Bar Chart */}
          <ComparisonBarChart results={results} />

          {/* ── Bank-wise Charges (Password Protected) ── */}
          <div className="border border-slate-200 rounded-xl overflow-hidden mt-6">
            <button onClick={() => setShowBanks((v) => !v)}
              className="w-full flex items-center justify-between px-5 py-4 bg-slate-50 hover:bg-slate-100 transition">
              <div className="flex items-center gap-3">
                <span className="font-semibold text-slate-800 text-[15px]">Bank-wise Processing Charges</span>
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: '#0f3d78' }}>
                  {bankRows.length} entries
                </span>
                {!bankUnlocked && <Lock className="w-3.5 h-3.5 text-slate-400" />}
                {bankUnlocked && <Unlock className="w-3.5 h-3.5 text-[#2ea690]" />}
              </div>
              {showBanks ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
            </button>

            {showBanks && (
              <div className="p-5">
                {!bankUnlocked ? (
                  /* Password gate */
                  <div className="max-w-sm mx-auto py-6 text-center">
                    <Lock className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-600 text-[14px] mb-4 font-medium">Enter password to edit bank charges</p>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <input
                          type={showPw ? 'text' : 'password'}
                          value={pwInput}
                          onChange={(e) => { setPwInput(e.target.value); setPwError(false); }}
                          onKeyDown={(e) => e.key === 'Enter' && tryUnlock()}
                          placeholder="Password"
                          className={`w-full h-10 px-3 pr-9 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-[#1e73be]/30 ${pwError ? 'border-red-400 bg-red-50' : 'border-slate-300'}`}
                        />
                        <button onClick={() => setShowPw((v) => !v)} className="absolute right-2.5 top-2.5 text-slate-400 hover:text-slate-600">
                          {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      <button onClick={tryUnlock}
                        className="px-4 h-10 rounded-lg text-white text-sm font-semibold hover:brightness-110"
                        style={{ backgroundColor: '#0f3d78' }}>
                        Unlock
                      </button>
                    </div>
                    {pwError && <p className="text-red-500 text-[12px] mt-2">Incorrect password</p>}
                  </div>
                ) : (
                  /* Editable bank table */
                  <>
                    <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                      <p className="text-slate-500 text-[13px]">
                        Reference loan: <strong>{fmt(refAmount)}</strong> (Option A). Edit and Save to update.
                      </p>
                      <div className="flex gap-2 flex-wrap">
                        <button onClick={addBank}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[13px] font-semibold text-white hover:brightness-110"
                          style={{ backgroundColor: '#0f3d78' }}>
                          <Plus className="w-3.5 h-3.5" /> Add Entry
                        </button>
                        <button onClick={saveBanks}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-[13px] font-semibold transition ${bankSaved ? 'border-[#2ea690] text-[#2ea690] bg-[#f0faf8]' : 'border-slate-300 text-slate-700 bg-white hover:bg-slate-50'}`}>
                          {bankSaved ? <><Check className="w-3.5 h-3.5" /> Saved!</> : <><Save className="w-3.5 h-3.5" /> Save</>}
                        </button>
                        <button onClick={() => setBankUnlocked(false)}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-300 text-[13px] text-slate-600 hover:bg-slate-50">
                          <Lock className="w-3.5 h-3.5" /> Lock
                        </button>
                      </div>
                    </div>

                    {/* Search bar */}
                    <div className="mb-3 relative max-w-xs">
                      <Search className="absolute left-3 top-2.5 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                      <input
                        type="text"
                        value={bankSearch}
                        onChange={(e) => setBankSearch(e.target.value)}
                        placeholder="Search by bank name or product…"
                        className="w-full h-9 pl-8 pr-8 border border-slate-300 rounded-lg text-[13px] outline-none focus:ring-2 focus:ring-[#1e73be]/30"
                      />
                      {bankSearch && (
                        <button onClick={() => setBankSearch('')}
                          className="absolute right-2.5 top-2 text-slate-400 hover:text-slate-600">
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    {bankSearch && (
                      <p className="text-[12px] text-slate-400 mb-2">
                        Showing {filteredBankRows.length} of {bankRows.length} entries matching "<strong>{bankSearch}</strong>"
                      </p>
                    )}

                    <div className="overflow-x-auto rounded-lg border border-slate-200">
                      <table className="text-[12px] min-w-[1260px] w-full">
                        <thead>
                          {/* Group headers */}
                          <tr className="bg-slate-100 border-b border-slate-200">
                            <th className="px-3 py-2 text-left text-slate-600 font-semibold" rowSpan={2} style={{ width: 120 }}>Bank Name</th>
                            <th className="px-3 py-2 text-left text-slate-600 font-semibold border-l border-slate-200" rowSpan={2} style={{ width: 130 }}>Product</th>
                            <th className="px-3 py-1.5 text-center text-[#1e73be] font-semibold border-l border-slate-200" colSpan={4}>Processing Fee</th>
                            <th className="px-3 py-1.5 text-center text-[#0f3d78] font-semibold border-l border-slate-200" colSpan={5}>Charges</th>
                            <th className="px-3 py-1.5 text-center text-[#b45309] font-semibold border-l border-slate-200" colSpan={4}>Insurance</th>
                            <th className="px-3 py-1.5 border-l border-slate-200" rowSpan={2} />
                          </tr>
                          <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 uppercase tracking-wide">
                            <th className="px-2 py-2 border-l border-slate-200 font-medium">Rate %</th>
                            <th className="px-2 py-2 font-medium">Min ₹</th>
                            <th className="px-2 py-2 font-medium">Cap ₹</th>
                            <th className="px-2 py-2 font-medium text-[#1e73be]">Est. Fee*</th>
                            <th className="px-2 py-2 border-l border-slate-200 font-medium">Legal ₹</th>
                            <th className="px-2 py-2 font-medium">Tech ₹</th>
                            <th className="px-2 py-2 font-medium">MODT %</th>
                            <th className="px-2 py-2 font-medium">NOI ₹</th>
                            <th className="px-2 py-2 font-medium">Doc ₹</th>
                            <th className="px-2 py-2 border-l border-slate-200 font-medium">Prop Ins</th>
                            <th className="px-2 py-2 font-medium">Yrs</th>
                            <th className="px-2 py-2 font-medium">₹/yr</th>
                            <th className="px-2 py-2 font-medium">Life Ins</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredBankRows.length === 0 ? (
                            <tr>
                              <td colSpan={16} className="px-5 py-8 text-center text-slate-400 text-[13px]">
                                No entries match "<strong>{bankSearch}</strong>". Try a different search.
                              </td>
                            </tr>
                          ) : filteredBankRows.map((bank, ri) => {
                            const estFee = calcProcFee(refAmount, bank);
                            return (
                              <tr key={bank.id} className={ri % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                                {/* Bank Name */}
                                <td className="px-2 py-2">
                                  <input value={bank.name} onChange={(e) => updateBank(bank.id, 'name', e.target.value)}
                                    className="w-full h-7 px-2 border border-slate-200 rounded text-[12px] outline-none focus:ring-1 focus:ring-[#1e73be]/40" />
                                </td>
                                {/* Product */}
                                <td className="px-2 py-2 border-l border-slate-100">
                                  <select
                                    value={bank.product || ''}
                                    onChange={(e) => updateBank(bank.id, 'product', e.target.value)}
                                    className="w-full h-7 px-1 border border-slate-200 rounded text-[12px] outline-none focus:ring-1 focus:ring-[#1e73be]/40 bg-white"
                                  >
                                    {LOAN_PRODUCT_OPTIONS.map((o) => (
                                      <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                  </select>
                                </td>
                                {/* Processing fee */}
                                <td className="px-2 py-2 border-l border-slate-100">
                                  <NumInput value={bank.procFeeRate} step={0.05} onChange={(v) => updateBank(bank.id, 'procFeeRate', v)} className="w-14" />
                                </td>
                                <td className="px-2 py-2">
                                  <NumInput value={bank.procFeeMin} step={1000} onChange={(v) => updateBank(bank.id, 'procFeeMin', v)} className="w-16" />
                                </td>
                                <td className="px-2 py-2">
                                  <NumInput value={bank.procFeeMax} step={1000} onChange={(v) => updateBank(bank.id, 'procFeeMax', v)} className="w-16" />
                                </td>
                                <td className="px-2 py-2 text-center font-semibold text-[#1e73be]">{fmt(estFee)}</td>
                                {/* Other charges */}
                                <td className="px-2 py-2 border-l border-slate-100">
                                  <NumInput value={bank.legalCharges} step={500} onChange={(v) => updateBank(bank.id, 'legalCharges', v)} className="w-16" />
                                </td>
                                <td className="px-2 py-2">
                                  <NumInput value={bank.techCharges} step={500} onChange={(v) => updateBank(bank.id, 'techCharges', v)} className="w-16" />
                                </td>
                                <td className="px-2 py-2">
                                  <NumInput value={bank.modtStampRate} step={0.05} onChange={(v) => updateBank(bank.id, 'modtStampRate', v)} className="w-14" />
                                </td>
                                <td className="px-2 py-2">
                                  <NumInput value={bank.noiCharge} step={100} onChange={(v) => updateBank(bank.id, 'noiCharge', v)} className="w-16" />
                                </td>
                                <td className="px-2 py-2">
                                  <NumInput value={bank.docCharges} step={500} onChange={(v) => updateBank(bank.id, 'docCharges', v)} className="w-16" />
                                </td>
                                {/* Property insurance */}
                                <td className="px-2 py-2 border-l border-slate-100 text-center">
                                  <InsBadge value={bank.propertyInsurance} onClick={() => toggleBankField(bank.id, 'propertyInsurance')} />
                                </td>
                                <td className="px-2 py-2">
                                  <NumInput value={bank.propInsYears} step={1} onChange={(v) => updateBank(bank.id, 'propInsYears', v)} className="w-10"
                                    style={{ opacity: bank.propertyInsurance === 'mandatory' ? 1 : 0.35 }} />
                                </td>
                                <td className="px-2 py-2">
                                  <NumInput value={bank.propInsPerYear} step={500} onChange={(v) => updateBank(bank.id, 'propInsPerYear', v)} className="w-16"
                                    style={{ opacity: bank.propertyInsurance === 'mandatory' ? 1 : 0.35 }} />
                                </td>
                                {/* Life insurance */}
                                <td className="px-2 py-2 text-center">
                                  <InsBadge value={bank.lifeInsurance} onClick={() => toggleBankField(bank.id, 'lifeInsurance')} />
                                </td>
                                <td className="px-2 py-2 border-l border-slate-100 text-center">
                                  <button onClick={() => removeBank(bank.id)} className="text-slate-300 hover:text-red-500 transition">
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <p className="text-[11px] text-slate-400 mt-2">* Est. Fee = Processing fee incl. 18% GST, computed for Option A loan amount. Cap ₹ = 0 means no cap.</p>
                  </>
                )}
              </div>
            )}
          </div>

          {/* ── Share + disclaimer ── */}
          <div className="mt-6 flex items-center justify-between flex-wrap gap-3">
            <p className="text-slate-400 text-xs">
              * EMI estimates are based on reducing-balance method. All charges are indicative — confirm with lender.
            </p>
            <button onClick={handleShareLink}
              className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-md border text-[13px] font-medium transition"
              style={linkCopied ? { borderColor: '#2ea690', color: '#2ea690', backgroundColor: '#f0faf8' } : { borderColor: '#cbd5e1', color: '#475569', backgroundColor: '#fff' }}>
              {linkCopied ? <><Check className="w-3.5 h-3.5" /> Link copied!</> : <><Link className="w-3.5 h-3.5" /> Copy link</>}
            </button>
          </div>

        </div>
      </section>
    </main>
  );
}
